/**
 * Authenticated requests to `/api/admin/*`.
 *
 * Primary auth: HttpOnly cookie `_mmd_adm` (same-origin or SameSite=None+Secure).
 * Fallback auth: Bearer token stored in localStorage — used when the browser
 * cannot send cross-site cookies (e.g. cross-origin dev setup or Safari ITP).
 */

import { getApiOrigin } from "./apiClientService";

const ADMIN_TOKEN_KEY = "admin_bearer_token";

export function setAdminSessionToken(token) {
  try {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getAdminSessionToken() {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function adminApiUrl(path) {
  const base = getApiOrigin();
  const cleaned = String(path || "").replace(/^\/+/, "");
  if (!cleaned) return `${base}/api/admin`;
  if (
    cleaned.startsWith("api/admin/") ||
    cleaned === "api/admin"
  ) {
    return `${base}/${cleaned}`;
  }
  return `${base}/api/admin/${cleaned}`;
}

/**
 * Fetch with credentials; JSON body serialized when passing a non-string object.
 * Attaches Bearer token from localStorage as fallback when cookies can't be sent.
 */
export async function adminFetch(path, options = {}) {
  const url = adminApiUrl(path);
  const {
    body,
    headers: rawHeaders = {},
    credentials = "include",
    ...rest
  } = options;

  const headers = new Headers(rawHeaders);

  // Attach Bearer token as fallback if we have one stored (cross-origin cookie fallback)
  if (!headers.has("Authorization")) {
    const storedToken = getAdminSessionToken();
    if (storedToken) {
      headers.set("Authorization", `Bearer ${storedToken}`);
    }
  }

  const init = {
    ...rest,
    credentials,
    headers,
  };

  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined && body !== null) {
    init.body = typeof body === "string" ? body : JSON.stringify(body);
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
  }

  return fetch(url, init);
}
